import axios from "axios";
import React, { useEffect, useState, useContext, useRef } from "react";
import Drawer from "../../components/UI/Drawer";
import Notification from "../../components/UI/Notification";
import ReactPaginate from "react-paginate";
import { Link, useNavigate } from "react-router-dom";
import DeleteButton from "../../components/UI/AlertDialog";
import DropdownMenu from "../../components/UI/DropdownMenu";
import {
  MoreVertical,
  User,
  Calendar,
  MapPin,
  Monitor,
  Globe,
  Clock,
  CheckCircle,
  XCircle,
  Smartphone,
  Info,
  ShieldCheck,
  X,
  LogIn,
  LogOut,
} from "lucide-react";
import { getToken } from "../../utils/tokenUtils";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";
import StatusBadge from "../../components/UI/StatusBadge";

// Actions dropdown used in the table rows
const ActionsColumn = React.memo(
  ({
    row,
    openMenu,
    setOpenMenu,
    onView,
    onEdit,
    onDelete,
    onApprove,
    onReject,
    activeTab,
  }) => {
    const buttonRef = useRef(null);

    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        {openMenu === row.id && (
          <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
            <button
              onClick={() => {
                onView(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors font-poppins"
            >
              View
            </button>
            {/* <button
              onClick={() => {
                onEdit(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors font-poppins"
            >
              Edit
            </button> */}
            <button
              onClick={() => {
                onApprove(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors font-poppins"
            >
              Approve
            </button>
            <button
              onClick={() => {
                onReject(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-yellow-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors font-poppins"
            >
              Reject
            </button>
            {/* <DeleteButton
              style={`text-red-600 hover:text-red-800 px-3 py-2`}
              onDelete={() => {
                onDelete?.length === 0 ? onDelete() : onDelete?.(row.id);
                setOpenMenu(null);
              }}
            /> */}
          </DropdownMenu>
        )}
      </div>
    );
  },
);

const CheckOutApprovalPage = () => {
  const token = getToken();
  const [activeTab, setActiveTab] = useState("checkOut"); // "checkIn" or "checkOut"
  const [checkInData, setCheckInData] = useState([]);
  const [checkOutData, setCheckOutData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [editAttendanceData, setEditAttendanceData] = useState({
    check_in_time: "",
    check_out_time: "",
  });
  const [openMenu, setOpenMenu] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const navigate = useNavigate();
  const { roles } = useContext(AuthContext);
  const getRoleBasedRouteLocal = (route) => getRoleBasedRoute(route, roles);

  const fetchData = async (type, page = 1) => {
    setLoading(true);
    try {
      const endpoint =
        type === "checkIn"
          ? `https://hrmspayroll.prepstripe.com/api/pending-checkin?page=${page}`
          : `https://hrmspayroll.prepstripe.com/api/pending-checkout?page=${page}`;
      const res = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (type === "checkIn") {
        setCheckInData(res.data.data.data);
      } else {
        setCheckOutData(res.data.data.data);
      }
      setPagination({
        currentPage: res.data?.data.current_page || 1,
        totalPages: res.data?.data.last_page || 1,
        totalItems: res.data?.data.total || 0,
        perPage: res.data?.data.per_page || 15,
      });
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab, currentPage);
  }, [activeTab, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchData(activeTab, newPage);
    }
  };

  const handleEditClick = (record) => {
    setSelectedRequest(record);
    const timeKey =
      activeTab === "checkIn" ? "check_in_time" : "check_out_time";
    const sourceValue = record?.attendance?.[timeKey] || "";
    setEditAttendanceData((prev) => ({
      ...prev,
      [timeKey]: toDateTimeLocalValue(sourceValue),
    }));
    setEditModalOpen(true);
  };

  // update particular attendance
  const handleUpdateAttendance = async (event) => {
    event.preventDefault();

    try {
      const timeKey =
        activeTab === "checkIn" ? "check_in_time" : "check_out_time";
      const payload = { [timeKey]: editAttendanceData[timeKey] };
      const attendanceId =
        selectedRequest?.attendance_id ||
        selectedRequest?.attendance?.id ||
        selectedRequest?.id;
      const response = await axios.put(
        `https://hrmspayroll.prepstripe.com/api/attendance/${attendanceId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setNotification(
        response?.data?.message || "Attendance updated successfully.",
      );
      setEditModalOpen(false);
      fetchData(activeTab, currentPage);
    } catch (error) {
      console.error(error);
      if (error.response) {
        setNotification(error.response.data.message || "Update failed");
      } else {
        setNotification("Something went wrong!");
      }
    }
  };

  // Handle Approve/Reject
  const handleApproval = async () => {
    if (!selectedRequest || !selectedStatus) return;

    setIsSubmitting(true);

    try {
      const reviewPath =
        activeTab === "checkIn" ? "review-checkin" : "review-checkout";
      const attendanceId =
        selectedRequest?.attendance_id ||
        selectedRequest?.attendance?.id ||
        selectedRequest?.id;
      const res = await axios.post(
        `https://hrmspayroll.prepstripe.com/api/attendance/${attendanceId}/${reviewPath}`,
        {
          approval_status: selectedStatus,
          manager_remark: approvalComment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      fetchData(activeTab, currentPage); // Refresh list
      closeApprovalModal();
      setIsDrawerOpen(false);
      setNotification(
        res?.data?.message || "Request status updated successfully",
      );
    } catch (err) {
      console.error("Error submitting approval:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Something went wrong";
      setNotification(errorMsg);
    } finally {
      setIsSubmitting(false);
      // setIsDrawerOpen(false);
    }
  };

  // delete particular attendance
  const deleteAttendance = async (attendanceId) => {
    if (!attendanceId) return;

    // // console.log('deleteAttendance called', { attendanceId, activeTab, currentPage });

    try {
      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/attendance/${attendanceId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            // Include authentication headers if needed
          },
          // credentials: 'include', // if using cookies for auth
        },
      );

      const result = await response.json();

      if (response.ok) {
        setNotification(result.message); // or use toast
        fetchData(activeTab, currentPage);
      } else {
        setNotification(
          `Error: ${result.error || "Failed to delete attendance"}`,
        );
      }
    } catch (error) {
      console.error("Delete failed:", error);
      setNotification(
        "An error occurred while deleting the attendance record.",
      );
    }
  };

  const openDrawerForDetails = (request) => {
    setSelectedRequest(request);
    setIsDrawerOpen(true);
  };

  const closeApprovalModal = () => {
    setIsModalOpen(false);
  };

  const openApprovalModal = (request, status) => {
    setSelectedRequest(request);
    setSelectedStatus(status);
    setApprovalComment(""); // Clear previous comment
    setIsModalOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRequest(null);
  };

  const formatTo12Hour = (timeStr) => {
    if (!timeStr) return "-";
    const date = new Date(timeStr);
    if (isNaN(date)) return "-";

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${hours}:${minutes} ${ampm}`;
  };
  const toDateTimeLocalValue = (dateTimeString) => {
    if (!dateTimeString) return "";
    const normalized =
      typeof dateTimeString === "string"
        ? dateTimeString.replace(" ", "T")
        : dateTimeString;
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return "";
    const tzOffsetMs = date.getTimezoneOffset() * 60000;
    const local = new Date(date.getTime() - tzOffsetMs);
    return local.toISOString().slice(0, 16);
  };

  const renderTable = (data) => (
    <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
      <table className="min-w-full table-auto md:table-fixed text-sm">
        <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
          <tr>
            <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
              Sr No.
            </th>
            <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
              Employee ID
            </th>
            <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
              Name
            </th>
            <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
              Date
            </th>
            <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
              {activeTab === "checkIn" ? "Check-In" : "Check-Out"}
            </th>
            <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
              Status
            </th>
            <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7" className="text-center py-4">
                Loading...
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center py-4">
                No Data Found
              </td>
            </tr>
          ) : (
            data.map((entry, index) => (
              <tr
                key={entry.id}
                className="border-b dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white hover:bg-gray-100"
              >
                <td className="py-2 px-4 text-center">
                  {(pagination.currentPage - 1) * pagination.perPage +
                    index +
                    1}
                </td>
                <td className="py-2 px-4 text-center">
                  {entry.attendance.employee_id}
                </td>
                <td className="py-2 px-4 text-center">
                  {entry.attendance.employee_name || "-"}
                </td>
                <td className="py-2 px-4 text-center">
                  {entry.attendance[
                    activeTab === "checkIn" ? "check_in_time" : "check_out_time"
                  ]
                    ? new Date(
                        entry.attendance[
                          activeTab === "checkIn"
                            ? "check_in_time"
                            : "check_out_time"
                        ],
                      ).toLocaleDateString()
                    : "-"}
                </td>
                <td className="py-2 px-4 text-center">
                  {formatTo12Hour(
                    entry.attendance[
                      activeTab === "checkIn"
                        ? "check_in_time"
                        : "check_out_time"
                    ],
                  )}
                </td>
                <td className="py-3 px-4 text-center">
                  <StatusBadge status={entry.approval_status || "pending"} />
                </td>
                <td className="py-2 px-4 text-center">
                  <ActionsColumn
                    row={entry}
                    openMenu={openMenu}
                    setOpenMenu={setOpenMenu}
                    onView={openDrawerForDetails}
                    onEdit={handleEditClick}
                    onDelete={() => deleteAttendance(entry.id)}
                    onApprove={(row) => openApprovalModal(row, "approved")}
                    onReject={(row) => openApprovalModal(row, "rejected")}
                    activeTab={activeTab}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      {/* Notification */}
      {notification && (
        <Notification
          message={notification}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">
                Check-In/Out Requests
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          Check-in/out Requests
        </h2>
      </div>
      {/* Tab Controls - Segmented Design */}
      <div className="flex p-1.5 bg-gray-100/80 dark:bg-gray-800/50 rounded-2xl w-fit mb-8 border border-gray-200/50 dark:border-gray-700/30 backdrop-blur-sm">
        <button
          onClick={() => {
            setActiveTab("checkIn");
            navigate(
              getRoleBasedRouteLocal(
                "/admin/attendance_management/pending-checkin",
              ),
            );
          }}
          className={`flex items-center gap-2.5 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 font-poppins ${
            activeTab === "checkIn"
              ? "bg-white dark:bg-gray-900 text-blue-600 shadow-sm ring-1 ring-gray-200/50 dark:ring-gray-700/50"
              : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800/80"
          }`}
        >
          <div
            className={`p-1 rounded-lg ${activeTab === "checkIn" ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600" : "bg-transparent"}`}
          >
            <LogIn className="w-4 h-4" />
          </div>
          Check-In Requests
        </button>
        <button
          onClick={() => {
            setActiveTab("checkOut");
            navigate(
              getRoleBasedRouteLocal(
                "/admin/attendance_management/pending-checkout",
              ),
            );
          }}
          className={`flex items-center gap-2.5 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 font-poppins ${
            activeTab === "checkOut"
              ? "bg-white dark:bg-gray-900 text-blue-600 shadow-sm ring-1 ring-gray-200/50 dark:ring-gray-700/50"
              : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800/80"
          }`}
        >
          <div
            className={`p-1 rounded-lg ${activeTab === "checkOut" ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600" : "bg-transparent"}`}
          >
            <LogOut className="w-4 h-4" />
          </div>
          Check-Out Requests
        </button>
      </div>

      {/* Table */}
      {renderTable(activeTab === "checkIn" ? checkInData : checkOutData)}

      {/* Pagination */}
      {!loading && (
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName="opacity-50 cursor-not-allowed"
          disabledLinkClassName="pointer-events-none"
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-4 font-poppins"
          pageClassName="mx-1 border rounded dark:text-white font-poppins"
          pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
        />
      )}

      {/* Drawer */}
      <Drawer
        border_radius="rounded-2xl"
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        showClose={false}
        title={`${activeTab === "checkIn" ? "Check-In" : "Check-Out"} Details`}
      >
        {selectedRequest && (
          <div className="h-full flex flex-col pt-4 font-poppins">
            {/* Header */}
            <div className="flex items-center justify-between mb-8 px-2">
              <div>
                <h3 className="text-2xl font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent font-poppins">
                  {activeTab === "checkIn" ? "Check-In" : "Check-Out"} Request
                </h3>
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1 font-poppins">
                  <Clock className="w-3.5 h-3.5" /> Request pending review
                </p>
              </div>
              <button
                onClick={closeDrawer}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors group"
              >
                <X className="w-6 h-6 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 space-y-6 scrollbar-hide">
              {/* Employee & Timing Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Employee Card */}
                <div className="bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-2xl border border-blue-100 dark:border-blue-800/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg text-blue-600 dark:text-blue-400">
                      <User className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                      Employee
                    </span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white truncate font-poppins">
                      {selectedRequest.attendance?.user?.name ||
                        selectedRequest?.attendance.employee_name ||
                        "N/A"}
                    </h4>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5 font-poppins">
                      <ShieldCheck className="w-3.5 h-3.5" />{" "}
                      {selectedRequest.attendance?.employee_id || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Timing Card */}
                <div className="bg-emerald-50/50 dark:bg-emerald-900/10 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-800/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg text-emerald-600 dark:text-emerald-400">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                      Time Segment
                    </span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white font-poppins">
                      {formatTo12Hour(
                        selectedRequest.attendance?.[
                          activeTab === "checkIn"
                            ? "check_in_time"
                            : "check_out_time"
                        ],
                      )}
                    </h4>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5 font-poppins">
                      <Clock className="w-3.5 h-3.5" />
                      {selectedRequest.attendance?.[
                        activeTab === "checkIn"
                          ? "check_in_time"
                          : "check_out_time"
                      ]
                        ? new Date(
                            selectedRequest.attendance[
                              activeTab === "checkIn"
                                ? "check_in_time"
                                : "check_out_time"
                            ],
                          ).toLocaleDateString(undefined, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Technical & Extra Details Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 space-y-5 font-poppins">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-2 font-poppins">
                  Technical Metadata
                </h5>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase">
                      <MapPin className="w-3.5 h-3.5 text-indigo-500" />{" "}
                      Location
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed italic">
                      {selectedRequest.attendance?.check_in_location ||
                        "Location data unavailable"}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase font-poppins">
                      <Globe className="w-3.5 h-3.5 text-blue-500" /> Network IP
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-poppins">
                      {selectedRequest.attendance?.ip_address || "None"}
                    </p>
                  </div>

                  <div className="space-y-1 col-span-full font-poppins">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase font-poppins">
                      <Monitor className="w-3.5 h-3.5 text-gray-500" /> User
                      Agent / Device
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 break-all leading-snug font-poppins">
                      {selectedRequest.attendance?.device_info || "N/A"}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase">
                      <Info className="w-3.5 h-3.5 text-amber-500" /> Status
                    </div>
                    <StatusBadge
                      status={selectedRequest.approval_status || "Pending"}
                    />
                  </div>
                </div>
              </div>

              {/* Image Section */}
              {selectedRequest.attendance?.check_in_image && (
                <div className="space-y-3 font-poppins">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-widest px-1">
                    <CheckCircle className="w-4 h-4 text-blue-500" /> Visual
                    Verification
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setImagePreviewOpen(true);
                    }}
                    className="relative group w-32 h-32 overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-blue-400 transition-colors cursor-zoom-in"
                  >
                    <img
                      src={`https://hrmspayroll.prepstripe.com/storage/attendance_images/${selectedRequest.attendance.check_in_image}`}
                      alt="Check-In"
                      className="w-full h-full object-cover rounded-xl shadow-lg"
                    />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Monitor className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-8 grid grid-cols-2 gap-4 pb-4 font-poppins">
              <button
                onClick={() => openApprovalModal(selectedRequest, "approved")}
                className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg shadow-emerald-200 dark:shadow-none transition-all hover:translate-y-[-2px] active:translate-y-[0] font-poppins"
              >
                <CheckCircle className="w-5 h-5" /> Approve Request
              </button>
              <button
                onClick={() => openApprovalModal(selectedRequest, "rejected")}
                className="flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg shadow-rose-200 dark:shadow-none transition-all hover:translate-y-[-2px] active:translate-y-[0] font-poppins"
              >
                <XCircle className="w-5 h-5" /> Reject Request
              </button>
            </div>
          </div>
        )}
      </Drawer>

      {/* edit checkin check out modal */}
      {editModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[10005] backdrop-blur-sm p-4 font-poppins">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden animate-in fade-in zoom-in duration-200 border dark:border-gray-800">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex items-center gap-3 bg-blue-50 dark:bg-gray-800/50 border-blue-100 dark:border-gray-700 font-poppins text-left">
              <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                <Clock className="w-6 h-6" />
              </div>
              <div className="text-left">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white leading-tight font-poppins">
                  Edit Attendance
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium font-poppins">
                  {selectedRequest.attendance?.user?.name ||
                    selectedRequest?.attendance?.employee_name ||
                    "Employee"}
                </p>
              </div>
            </div>

            <form onSubmit={handleUpdateAttendance} className="font-poppins">
              {/* Modal Body */}
              <div className="p-6">
                <label className="flex text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wider font-poppins">
                  {activeTab === "checkIn" ? "Check-In" : "Check-Out"} Time
                </label>
                <div className="relative group text-left">
                  <input
                    type="datetime-local"
                    value={
                      editAttendanceData[
                        activeTab === "checkIn"
                          ? "check_in_time"
                          : "check_out_time"
                      ] || ""
                    }
                    onChange={(e) =>
                      setEditAttendanceData((prev) => ({
                        ...prev,
                        [activeTab === "checkIn"
                          ? "check_in_time"
                          : "check_out_time"]: e.target.value,
                      }))
                    }
                    required
                    className="w-full border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl px-4 py-3 transition-all outline-none bg-gray-50/50 dark:bg-gray-800/50 text-gray-700 dark:text-white font-medium font-poppins"
                  />
                </div>
                <p className="flex text-[10px] text-gray-400 dark:text-gray-500 mt-2 italic font-poppins">
                  * Modifying this will affect the employee's attendance record.
                </p>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 flex items-center justify-end gap-3 border-t dark:border-gray-700 font-poppins">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all font-poppins uppercase tracking-wide"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  className="px-8 py-2.5 text-sm font-semibold text-white rounded-xl bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 dark:shadow-none transition-all transform hover:scale-105 active:scale-95 uppercase tracking-wide font-poppins"
                >
                  UPDATE
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for Approval with remark */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[1100] p-4 animate-in fade-in duration-300">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-300 font-poppins border dark:border-gray-800">
            {/* Header */}
            <div
              className={`px-6 py-4 border-b flex items-center gap-3 font-poppins text-left `}
            >
              <div
                className={`p-2 rounded-full ${
                  selectedStatus === "approved"
                    ? "bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400"
                    : "bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400"
                }`}
              >
                {selectedStatus === "approved" ? (
                  <CheckCircle className="w-6 h-6" />
                ) : (
                  <XCircle className="w-6 h-6" />
                )}
              </div>
              <div className="text-left">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white leading-tight font-poppins">
                  {selectedStatus === "approved"
                    ? "Approve Request"
                    : "Reject Request"}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium font-poppins tracking-wide">
                  {selectedRequest?.attendance?.user?.name ||
                    selectedRequest?.full_name ||
                    "Employee"}
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 text-left">
              <label
                htmlFor="approver-comments-modal"
                className="flex text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wider font-poppins"
              >
                Manager Remark
              </label>
              <div className="relative group">
                <textarea
                  id="approver-comments-modal"
                  rows={4}
                  className="w-full p-4 text-base text-gray-700 dark:text-white border-[2px] border-gray-100 dark:border-gray-700 dark:bg-gray-800/50 rounded-lg focus:outline-none focus:border-gray-400 transition-all resize-none placeholder:text-gray-400 font-poppins"
                  placeholder="Write your reason or feedback here..."
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                />
              </div>
              <p className="mt-2 text-[10px] italic text-gray-400 dark:text-gray-500 font-medium text-left">
                * This remark will be visible to the employee.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 flex items-center justify-end gap-3 border-t dark:border-gray-700 font-poppins">
              <button
                onClick={closeApprovalModal}
                className="px-6 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all font-poppins uppercase tracking-wide"
              >
                CANCEL
              </button>
              <button
                onClick={handleApproval}
                disabled={isSubmitting}
                className={`px-8 py-2.5 text-sm font-semibold text-white rounded-lg transition-all transform hover:scale-105 active:scale-95 uppercase tracking-wide font-poppins ${
                  selectedStatus === "approved"
                    ? "bg-green-600 hover:bg-green-700 shadow-green-200 dark:shadow-none"
                    : "bg-red-600 hover:bg-red-700 shadow-red-200 dark:shadow-none"
                } ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {isSubmitting
                  ? "PROCESSING..."
                  : selectedStatus === "approved"
                    ? "APPROVE"
                    : "REJECT"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Full Image Preview Modal */}
      {imagePreviewOpen && selectedRequest?.attendance?.check_in_image && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-xl z-[10001] flex items-center justify-center p-4 cursor-default"
          onClick={() => setImagePreviewOpen(false)}
        >
          <div className="relative max-w-4xl w-full h-full flex items-center justify-center flex-col gap-4">
            <button
              className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-red-600 rounded-full text-white transition-all transform hover:scale-110 shadow-lg z-10"
              onClick={() => setImagePreviewOpen(false)}
            >
              <X className="w-8 h-8 font-bold" />
            </button>
            <img
              src={`https://hrmspayroll.prepstripe.com/storage/attendance_images/${selectedRequest.attendance.check_in_image}`}
              alt="Full Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="text-white font-poppins text-center bg-black/50 px-6 py-2 rounded-full backdrop-blur-sm">
              {selectedRequest.attendance?.user?.name || "Attendance Proof"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckOutApprovalPage;
